import {create} from 'zustand';
import {createJSONStorage, persist} from 'zustand/middleware';
import * as dateFns from 'date-fns';
import {OfferType} from '@deezer/next/services/api/Starmania/requests/album/types';
import {getUTCDate} from '@deezer/next/shared/utils/date';
import {MAX_DATE, MIN_DATE} from '@constants/dates';
import storage from '@deezer/next/shared/utils/storage';

const defaultStartDate = dateFns.subDays(getUTCDate(), 7);
const defaultEndDate = dateFns.subDays(getUTCDate(), 1);

export type AnalyticsFiltersState = {
	selectedCountry: string;
	selectedOffer: OfferType;
	startDate: Date;
	endDate: Date;
	setFilters: (params: Partial<AnalyticsFiltersState>) => void;
	resetToDefaults: () => void;
};

type AnalyticsFiltersPersisted = {
	selectedCountry: string;
	selectedOffer: OfferType;
	startDate: string;
	endDate: string;
};

export type UiAnalyticsFilters = Pick<
	AnalyticsFiltersState,
	'selectedCountry' | 'selectedOffer' | 'startDate' | 'endDate'
>;

export const ANALYTICS_DEFAULT_FILTERS: UiAnalyticsFilters = {
	selectedCountry: 'ww',
	selectedOffer: OfferType.all,
	startDate: defaultStartDate,
	endDate: defaultEndDate,
};

const clampDates = (start?: Date, end?: Date) => {
	let s = start || defaultStartDate;
	let e = end || defaultEndDate;

	if (dateFns.isAfter(s, e) && !dateFns.isSameDay(s, e)) {
		s = defaultStartDate;
		e = defaultEndDate;
	}

	if (dateFns.isBefore(s, MIN_DATE)) s = MIN_DATE;
	if (dateFns.isAfter(e, MAX_DATE)) e = MAX_DATE;

	return {start: s, end: e};
};

const stringLocalStorage = {
	getItem: (name: string): string | null => {
		const value = storage.get<string | undefined>(name);
		return value ?? null;
	},
	setItem: (name: string, value: string): void => {
		storage.set(name, value);
	},
	removeItem: (name: string): void => {
		storage.remove(name);
	},
};

export const useAnalyticsFiltersStore = create<AnalyticsFiltersState>()(
	persist(
		set => ({
			selectedCountry: storage.get('country', 'ww'),
			selectedOffer: storage.get('offer', OfferType.all) as OfferType,
			startDate: (() => {
				const stored = storage.get<string | undefined>('startDate');
				return stored ? dateFns.parseISO(stored) : defaultStartDate;
			})(),
			endDate: (() => {
				const stored = storage.get<string | undefined>('endDate');
				return stored ? dateFns.parseISO(stored) : defaultEndDate;
			})(),

			setFilters: params =>
				set(current => {
					const nextCountry = params.selectedCountry ?? current.selectedCountry;
					const nextOffer = params.selectedOffer ?? current.selectedOffer;
					const rawStart = params.startDate ?? current.startDate;
					const rawEnd = params.endDate ?? current.endDate;
					const {start, end} = clampDates(rawStart, rawEnd);

					return {
						...current,
						selectedCountry: nextCountry,
						selectedOffer: nextOffer,
						startDate: start,
						endDate: end,
					};
				}),

			resetToDefaults: () => set({...ANALYTICS_DEFAULT_FILTERS}),
		}),
		{
			name: 'analytics-filters',
			storage: createJSONStorage<AnalyticsFiltersPersisted>(
				() => stringLocalStorage,
			),
			partialize: state => ({
				selectedCountry: state.selectedCountry,
				selectedOffer: state.selectedOffer,
				startDate: state.startDate.toISOString(),
				endDate: state.endDate.toISOString(),
			}),
			merge: (persisted, current) => {
				const p = persisted as AnalyticsFiltersPersisted;

				const startDate = p.startDate
					? dateFns.parseISO(p.startDate)
					: current.startDate;
				const endDate = p.endDate
					? dateFns.parseISO(p.endDate)
					: current.endDate;

				return {
					...current,
					...p,
					startDate,
					endDate,
				};
			},
		},
	),
);
